import { VOID_REASONS, composeVoidReason } from '@accounting/shared';
import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import {
  useAccountDuplicates,
  useMoveJournalLinesBulk,
  useVoidJournalEntriesBulk,
  type DuplicateEntrySide,
  type DuplicatePair,
} from '../../hooks/useAccountDuplicates';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

/** Canonical label so duplicate voids are countable in the audit log. */
const DUPLICATE_VOID_LABEL = VOID_REASONS.find((r) => r.code === 'duplicate')!.label;

/**
 * Find-duplicates tool. Pick two accounts; the page lists journal entries that
 * appear on both with matching date + absolute amount, so the user can bulk-void
 * one side.
 *
 * Use case: QBO import landed on accounts 2018/2019, Plaid now lands on 2002 —
 * every overlapping transaction is double-booked. Compare 2018 vs 2002, select
 * the 2018-side rows you want gone, click "Void selected (left)".
 */
export const Route = createFileRoute('/_auth/accounts/duplicates')({
  validateSearch: (search: Record<string, unknown>): { left?: string; right?: string } => ({
    left: typeof search.left === 'string' ? search.left : undefined,
    right: typeof search.right === 'string' ? search.right : undefined,
  }),
  component: DuplicatesPage,
});

function DuplicatesPage() {
  const companyId = useActiveCompanyId();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: accounts = [] } = useAccounts(companyId);

  const left = search.left ?? '';
  const right = search.right ?? '';
  const setLeft = (v: string): void =>
    void navigate({ search: (p) => ({ ...p, left: v || undefined }) });
  const setRight = (v: string): void =>
    void navigate({ search: (p) => ({ ...p, right: v || undefined }) });

  const dupQuery = useAccountDuplicates(companyId, left || null, right || null);
  const voidBulk = useVoidJournalEntriesBulk(companyId);
  const moveBulk = useMoveJournalLinesBulk(companyId);

  const [selectedLeft, setSelectedLeft] = useState<Set<string>>(new Set());
  const [selectedRight, setSelectedRight] = useState<Set<string>>(new Set());
  const [voidMsg, setVoidMsg] = useState<string | null>(null);
  const [moveMsg, setMoveMsg] = useState<string | null>(null);

  const pairs = dupQuery.data?.pairs ?? [];
  const unmatchedLeft = dupQuery.data?.unmatchedLeft ?? [];
  const unmatchedRight = dupQuery.data?.unmatchedRight ?? [];

  const leftAcct = dupQuery.data?.leftAccount;
  const rightAcct = dupQuery.data?.rightAccount;

  // Distinct reconciliation IDs that are locking entries on either side. Surfaces
  // a help banner so the user has a one-click path to void them.
  const lockingReconIds = useMemo(() => {
    const ids = new Set<string>();
    const collect = (e: DuplicateEntrySide): void => {
      if (e.isLocked && e.lockedByReconciliationId) ids.add(e.lockedByReconciliationId);
    };
    for (const p of pairs) {
      collect(p.left);
      collect(p.right);
    }
    for (const e of unmatchedLeft) collect(e);
    for (const e of unmatchedRight) collect(e);
    return [...ids];
  }, [pairs, unmatchedLeft, unmatchedRight]);

  // For each pair, the "voidable" side is the one with an UNLOCKED entry.
  const voidableLeft = useMemo(
    () => pairs.filter((p) => !p.left.isLocked),
    [pairs],
  );
  const voidableRight = useMemo(
    () => pairs.filter((p) => !p.right.isLocked),
    [pairs],
  );

  const selectAllLeft = (): void =>
    setSelectedLeft(new Set(voidableLeft.map((p) => p.left.journalEntryId)));
  const selectAllRight = (): void =>
    setSelectedRight(new Set(voidableRight.map((p) => p.right.journalEntryId)));
  const clearSelections = (): void => {
    setSelectedLeft(new Set());
    setSelectedRight(new Set());
  };

  /**
   * Move every UNMATCHED entry from one account to the other.
   * Direction "leftToRight" picks the unmatched-left entries and re-points their
   * line on the left account to the right account. Direction "rightToLeft"
   * does the opposite. Pairs are NOT touched (those need void, not move —
   * moving would create a same-account duplicate).
   */
  const handleMoveUnmatched = (direction: 'leftToRight' | 'rightToLeft'): void => {
    if (!leftAcct || !rightAcct) return;
    const entries = direction === 'leftToRight' ? unmatchedLeft : unmatchedRight;
    const movable = entries.filter((e) => !e.isLocked);
    if (movable.length === 0) return;
    const sourceAcct = direction === 'leftToRight' ? leftAcct : rightAcct;
    const targetAcct = direction === 'leftToRight' ? rightAcct : leftAcct;
    if (
      !confirm(
        `Move ${movable.length} entr${movable.length === 1 ? 'y' : 'ies'} from ${sourceAcct.code} ${sourceAcct.name} to ${targetAcct.code} ${targetAcct.name}? The journal entries stay the same — only the account on the affected line changes. Locked entries are skipped.`,
      )
    )
      return;
    setMoveMsg(null);
    moveBulk.mutate(
      {
        journalEntryIds: movable.map((e) => e.journalEntryId),
        sourceAccountId: sourceAcct.id,
        targetAccountId: targetAcct.id,
      },
      {
        onSuccess: (r) => {
          const parts: string[] = [`Moved ${r.succeeded.length}`];
          if (r.failed.length > 0) parts.push(`${r.failed.length} failed`);
          setMoveMsg(parts.join(' · '));
        },
        onError: (e) => {
          const msg =
            e instanceof ApiError
              ? (e.body as { error?: string } | null)?.error ?? e.message
              : (e as Error).message;
          setMoveMsg(`Move failed: ${msg}`);
        },
      },
    );
  };

  const handleVoid = (side: 'left' | 'right'): void => {
    const ids = side === 'left' ? [...selectedLeft] : [...selectedRight];
    if (ids.length === 0) return;
    const label = side === 'left'
      ? `${leftAcct?.code} ${leftAcct?.name ?? ''}`
      : `${rightAcct?.code} ${rightAcct?.name ?? ''}`;
    if (
      !confirm(
        `Void ${ids.length} entr${ids.length === 1 ? 'y' : 'ies'} on ${label}? This is reversible (you can re-create entries manually) but locked / reconciled entries will be skipped.`,
      )
    )
      return;
    setVoidMsg(null);
    voidBulk.mutate(
      {
        journalEntryIds: ids,
        voidReason: composeVoidReason(
          DUPLICATE_VOID_LABEL,
          `account duplicate cleanup vs ${side === 'left' ? rightAcct?.code : leftAcct?.code}`,
        ),
      },
      {
        onSuccess: (r) => {
          if (side === 'left') setSelectedLeft(new Set());
          else setSelectedRight(new Set());
          const parts: string[] = [`Voided ${r.succeeded.length}`];
          if (r.failed.length > 0) parts.push(`${r.failed.length} failed`);
          setVoidMsg(parts.join(' · '));
        },
        onError: (e) => {
          const msg = e instanceof ApiError ? (e.body as { error?: string } | null)?.error ?? e.message : (e as Error).message;
          setVoidMsg(`Void failed: ${msg}`);
        },
      },
    );
  };

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <Link to="/accounts" style={{ fontSize: 14 }}>← Chart of accounts</Link>
      <h1 style={{ fontSize: 24, margin: '8px 0 4px' }}>Find duplicate entries</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13, maxWidth: 720 }}>
        Pick two accounts. The tool pairs journal entries by date + absolute amount, so you can
        bulk-void the ones you want gone. Typical use: an import landed on one account, a
        feed now lands on a different account, and you have double-booked transactions to clean up.
      </p>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Left account
            </label>
            <AccountPicker
              accounts={accounts}
              value={left}
              onChange={(v) => {
                setLeft(v);
                clearSelections();
              }}
              placeholder="Pick the account whose entries are likely duplicates"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Right account
            </label>
            <AccountPicker
              accounts={accounts}
              value={right}
              onChange={(v) => {
                setRight(v);
                clearSelections();
              }}
              placeholder="Pick the account where the same activity also landed"
            />
          </div>
        </div>
      </div>

      {dupQuery.isLoading && left && right && (
        <p className="muted">Comparing…</p>
      )}

      {dupQuery.error && (
        <div className="field-error" style={{ marginBottom: 16 }}>
          {dupQuery.error instanceof ApiError
            ? (dupQuery.error.body as { error?: string } | null)?.error ?? dupQuery.error.message
            : (dupQuery.error as Error).message}
        </div>
      )}

      {voidMsg && (
        <div className="card" style={{ marginBottom: 16, padding: 10, fontSize: 13 }}>
          {voidMsg}
        </div>
      )}
      {moveMsg && (
        <div className="card" style={{ marginBottom: 16, padding: 10, fontSize: 13 }}>
          {moveMsg}
        </div>
      )}

      {dupQuery.data && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 16,
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: 12,
            }}
          >
            <strong>{pairs.length}</strong> matched pair{pairs.length === 1 ? '' : 's'}
            <span className="muted">·</span>
            <span className="muted">
              {unmatchedLeft.length} only on {leftAcct?.code}, {unmatchedRight.length} only on {rightAcct?.code}
            </span>
          </div>

          {lockingReconIds.length > 0 && (
            <div
              className="card"
              style={{
                padding: 10,
                marginBottom: 12,
                background: 'var(--bg-surface)',
                fontSize: 13,
                borderLeft: '3px solid var(--warn, #c69a00)',
              }}
            >
              <strong>{lockingReconIds.length} reconciliation{lockingReconIds.length === 1 ? '' : 's'}</strong>{' '}
              are locking entries below. Locked entries can't be moved or voided directly — open
              each reconciliation and click "Void reconciliation" to unlock them, then refresh this page.
              <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {lockingReconIds.map((id) => (
                  <Link
                    key={id}
                    to="/reconcile/$id"
                    params={{ id }}
                    className="btn-secondary"
                    style={{ textDecoration: 'none', fontSize: 12, padding: '2px 8px' }}
                  >
                    Open reconciliation
                  </Link>
                ))}
              </div>
            </div>
          )}

          {pairs.length > 0 && (
            <div className="card" style={{ padding: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={selectAllLeft}
                  disabled={voidableLeft.length === 0}
                  title={`Select every unlocked left-side (${leftAcct?.code}) entry`}
                >
                  Select all left ({voidableLeft.length})
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={selectAllRight}
                  disabled={voidableRight.length === 0}
                  title={`Select every unlocked right-side (${rightAcct?.code}) entry`}
                >
                  Select all right ({voidableRight.length})
                </button>
                <button type="button" className="btn-secondary" onClick={clearSelections}>
                  Clear selections
                </button>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => handleVoid('left')}
                    disabled={selectedLeft.size === 0 || voidBulk.isPending}
                    title={`Void the selected ${leftAcct?.code} entries`}
                  >
                    {voidBulk.isPending ? 'Voiding…' : `Void ${selectedLeft.size} left`}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => handleVoid('right')}
                    disabled={selectedRight.size === 0 || voidBulk.isPending}
                    title={`Void the selected ${rightAcct?.code} entries`}
                  >
                    {voidBulk.isPending ? 'Voiding…' : `Void ${selectedRight.size} right`}
                  </button>
                </div>
              </div>

              <PairsTable
                pairs={pairs}
                leftLabel={`${leftAcct?.code} ${leftAcct?.name ?? ''}`}
                rightLabel={`${rightAcct?.code} ${rightAcct?.name ?? ''}`}
                selectedLeft={selectedLeft}
                selectedRight={selectedRight}
                onToggleLeft={(id) =>
                  setSelectedLeft((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })
                }
                onToggleRight={(id) =>
                  setSelectedRight((prev) => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id);
                    else next.add(id);
                    return next;
                  })
                }
              />
            </div>
          )}

          {(unmatchedLeft.length > 0 || unmatchedRight.length > 0) && (
            <details open>
              <summary style={{ cursor: 'pointer', fontSize: 14, marginBottom: 8 }}>
                Unmatched entries ({unmatchedLeft.length + unmatchedRight.length}) — these are
                only on one side. Move them to the other account if they belong there.
              </summary>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
                <UnmatchedList
                  title={`Only on ${leftAcct?.code} ${leftAcct?.name ?? ''}`}
                  entries={unmatchedLeft}
                  moveLabel={
                    leftAcct && rightAcct && unmatchedLeft.some((e) => !e.isLocked)
                      ? `Move all to ${rightAcct.code} ${rightAcct.name}`
                      : null
                  }
                  onMove={() => handleMoveUnmatched('leftToRight')}
                  moving={moveBulk.isPending}
                />
                <UnmatchedList
                  title={`Only on ${rightAcct?.code} ${rightAcct?.name ?? ''}`}
                  entries={unmatchedRight}
                  moveLabel={
                    leftAcct && rightAcct && unmatchedRight.some((e) => !e.isLocked)
                      ? `Move all to ${leftAcct.code} ${leftAcct.name}`
                      : null
                  }
                  onMove={() => handleMoveUnmatched('rightToLeft')}
                  moving={moveBulk.isPending}
                />
              </div>
            </details>
          )}

          {pairs.length === 0 && (
            <p className="muted" style={{ marginTop: 12 }}>
              No matching pairs across these two accounts. If you expected pairs, double-check the
              account selection and date range.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function PairsTable({
  pairs,
  leftLabel,
  rightLabel,
  selectedLeft,
  selectedRight,
  onToggleLeft,
  onToggleRight,
}: {
  pairs: DuplicatePair[];
  leftLabel: string;
  rightLabel: string;
  selectedLeft: Set<string>;
  selectedRight: Set<string>;
  onToggleLeft: (journalEntryId: string) => void;
  onToggleRight: (journalEntryId: string) => void;
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ width: 90 }}>Date</th>
            <th className="right" style={{ width: 100 }}>Amount</th>
            <th colSpan={2}>{leftLabel}</th>
            <th colSpan={2}>{rightLabel}</th>
          </tr>
        </thead>
        <tbody>
          {pairs.map((p) => (
            <tr key={p.key}>
              <td>{formatAccountingDate(p.left.date)}</td>
              <td className="right">{formatCurrency(p.left.amount)}</td>
              <SideCells side={p.left} selected={selectedLeft.has(p.left.journalEntryId)} onToggle={onToggleLeft} />
              <SideCells side={p.right} selected={selectedRight.has(p.right.journalEntryId)} onToggle={onToggleRight} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SideCells({
  side,
  selected,
  onToggle,
}: {
  side: DuplicateEntrySide;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <>
      <td style={{ width: 28 }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(side.journalEntryId)}
          disabled={side.isLocked}
          title={side.isLocked ? 'Locked by a completed reconciliation — void the reconciliation first' : 'Select for bulk void'}
        />
      </td>
      <td>
        <Link to="/journal/$id" params={{ id: side.journalEntryId }}>
          {side.entryNumber}
        </Link>
        <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
          {side.side === 'debit' ? 'DR' : 'CR'}
        </span>
        {side.isLocked && side.lockedByReconciliationId && (
          <Link
            to="/reconcile/$id"
            params={{ id: side.lockedByReconciliationId }}
            className="tag"
            style={{ marginLeft: 6, fontSize: 11, textDecoration: 'none' }}
            title="Void this reconciliation to unlock the entry"
          >
            locked → open recon
          </Link>
        )}
        {side.isLocked && !side.lockedByReconciliationId && (
          <span className="tag" style={{ marginLeft: 6, fontSize: 11 }}>locked</span>
        )}
        {side.memo && (
          <div className="muted" style={{ fontSize: 11, fontStyle: 'italic' }}>
            "{side.memo}"
          </div>
        )}
      </td>
    </>
  );
}

function UnmatchedList({
  title,
  entries,
  moveLabel,
  onMove,
  moving,
}: {
  title: string;
  entries: DuplicateEntrySide[];
  moveLabel?: string | null;
  onMove?: () => void;
  moving?: boolean;
}) {
  if (entries.length === 0) {
    return (
      <div className="card" style={{ padding: 10 }}>
        <strong>{title}</strong>
        <p className="muted" style={{ marginTop: 4, marginBottom: 0, fontSize: 12 }}>
          (None — every entry on this account has a match on the other side.)
        </p>
      </div>
    );
  }
  const movable = entries.filter((e) => !e.isLocked);
  return (
    <div className="card" style={{ padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div>
          <strong>{title}</strong>{' '}
          <span className="muted" style={{ fontSize: 12 }}>({entries.length})</span>
        </div>
        {moveLabel && onMove && (
          <button
            type="button"
            className="btn-secondary"
            onClick={onMove}
            disabled={moving}
            title={`Re-point the line on this account to the other account for ${movable.length} unlocked entr${movable.length === 1 ? 'y' : 'ies'}. Journal entries are preserved, only the account on that line changes.`}
            style={{ fontSize: 12, padding: '4px 10px' }}
          >
            {moving ? 'Moving…' : `${moveLabel} (${movable.length})`}
          </button>
        )}
      </div>
      <ul style={{ marginTop: 6, paddingLeft: 20, fontSize: 12 }}>
        {entries.slice(0, 50).map((e) => (
          <li key={e.journalEntryId}>
            <Link to="/journal/$id" params={{ id: e.journalEntryId }}>{e.entryNumber}</Link>
            {' · '}
            {formatAccountingDate(e.date)}
            {' · '}
            {formatCurrency(e.amount)}
            {e.isLocked && <span className="tag" style={{ marginLeft: 6, fontSize: 11 }}>locked</span>}
            {e.memo && <span className="muted"> "{e.memo}"</span>}
          </li>
        ))}
        {entries.length > 50 && (
          <li className="muted">… and {entries.length - 50} more</li>
        )}
      </ul>
    </div>
  );
}
